import { useEffect, useState } from "react"
import { observer } from "mobx-react"
import { useStore } from "stores/utils/use-store"
import {
  Button,
  DatePicker,
  Divider,
  Form,
  Input,
  InputNumber,
  Modal,
  Popconfirm,
  Space,
  Table,
  Typography,
} from "antd"
import type { ColumnsType } from "antd/lib/table"
import { notifications } from "notifications"
import moment from "moment"
import { IMarketplaceCohort } from "../api/marketplace-cohorts.call"

const { Title, Text } = Typography

const RULE_KEYS = [
  { key: "max_usd_for_redeem_daily_global", label: "Max USD (Daily, Global)", isDollar: true },
  { key: "max_usd_for_redeem_daily_per_user", label: "Max USD (Daily, Per User)", isDollar: true },
  { key: "number_of_users_daily_redemption_attempts", label: "Max Daily Attempts (Per User)", isDollar: false },
  {
    key: "utc_hours_for_marketplace_redemption_limit_reset",
    label: "UTC Reset Hour (0–23)",
    isDollar: false,
    min: 0,
    max: 23,
  },
]

type RuleFormValues = {
  max_usd_for_redeem_daily_global?: number | null
  max_usd_for_redeem_daily_per_user?: number | null
  number_of_users_daily_redemption_attempts?: number | null
  utc_hours_for_marketplace_redemption_limit_reset?: number | null
}

type CohortFormValues = {
  name: string
  accountCreatedFrom?: moment.Moment | null
  accountCreatedTo?: moment.Moment | null
} & RuleFormValues

function rulesToForm(rules: Record<string, string>): RuleFormValues {
  const toNum = (k: string) => {
    const v = rules[k]

    return v !== undefined ? Number(v) : null
  }

  return {
    max_usd_for_redeem_daily_global: toNum("max_usd_for_redeem_daily_global"),
    max_usd_for_redeem_daily_per_user: toNum("max_usd_for_redeem_daily_per_user"),
    number_of_users_daily_redemption_attempts: toNum("number_of_users_daily_redemption_attempts"),
    utc_hours_for_marketplace_redemption_limit_reset: toNum("utc_hours_for_marketplace_redemption_limit_reset"),
  }
}

function formToRules(values: RuleFormValues): Record<string, string> {
  const result: Record<string, string> = {}
  const entries: [string, number | null | undefined][] = [
    ["max_usd_for_redeem_daily_global", values.max_usd_for_redeem_daily_global],
    ["max_usd_for_redeem_daily_per_user", values.max_usd_for_redeem_daily_per_user],
    ["number_of_users_daily_redemption_attempts", values.number_of_users_daily_redemption_attempts],
    ["utc_hours_for_marketplace_redemption_limit_reset", values.utc_hours_for_marketplace_redemption_limit_reset],
  ]

  for (const [k, v] of entries) {
    if (v !== null && v !== undefined) {
      result[k] = String(v)
    }
  }

  return result
}

function formatDate(iso: string | null): string {
  if (!iso) return "—"

  return new Date(iso).toLocaleDateString("en-US", { year: "numeric", month: "short", day: "numeric" })
}

function summariseRules(rules: Record<string, string>): string {
  const parts = Object.entries(rules).map(([k, v]) => {
    const def = RULE_KEYS.find(r => r.key === k)

    return def ? `${def.label}: ${def.isDollar ? `$${v}` : v}` : `${k}: ${v}`
  })

  return parts.length ? parts.join(", ") : "—"
}

export const MarketplaceCohorts = observer(() => {
  const { dataStore: { marketplaceCohortsStore } } = useStore()
  const [form] = Form.useForm<CohortFormValues>()
  const [modalOpen, setModalOpen] = useState(false)
  const [editingCohort, setEditingCohort] = useState<IMarketplaceCohort | null>(null)

  useEffect(() => {
    marketplaceCohortsStore.load()
  }, [marketplaceCohortsStore])

  const openCreate = () => {
    setEditingCohort(null)
    form.resetFields()
    setModalOpen(true)
  }

  const openEdit = (cohort: IMarketplaceCohort) => {
    setEditingCohort(cohort)
    form.setFieldsValue({
      name: cohort.name,
      accountCreatedFrom: cohort.accountCreatedFrom ? moment(cohort.accountCreatedFrom) : null,
      accountCreatedTo: cohort.accountCreatedTo ? moment(cohort.accountCreatedTo) : null,
      ...rulesToForm(cohort.rules),
    })
    setModalOpen(true)
  }

  const handleDelete = async(id: string) => {
    marketplaceCohortsStore.funcAfterSuccess = async() => {
      notifications.success("Cohort deleted")
      await marketplaceCohortsStore.load()
    }
    await marketplaceCohortsStore.remove(id)
  }

  const handleSubmit = async(values: CohortFormValues) => {
    const rules = formToRules(values)
    const payload = {
      name: values.name,
      accountCreatedFrom: values.accountCreatedFrom ? values.accountCreatedFrom.toISOString() : null,
      accountCreatedTo: values.accountCreatedTo ? values.accountCreatedTo.toISOString() : null,
      rules,
    }

    marketplaceCohortsStore.funcAfterSuccess = async() => {
      notifications.success(editingCohort ? "Cohort updated" : "Cohort created")
      setModalOpen(false)
      await marketplaceCohortsStore.load()
    }

    if (editingCohort) {
      await marketplaceCohortsStore.update(editingCohort.id, payload)
    } else {
      await marketplaceCohortsStore.create(payload)
    }
  }

  const columns: ColumnsType<IMarketplaceCohort> = [
    {
      title: "Name",
      dataIndex: "name",
      key: "name",
    },
    {
      title: "Account Created From",
      dataIndex: "accountCreatedFrom",
      key: "accountCreatedFrom",
      render: (val: string | null) => formatDate(val),
    },
    {
      title: "Account Created To",
      dataIndex: "accountCreatedTo",
      key: "accountCreatedTo",
      render: (val: string | null) => formatDate(val),
    },
    {
      title: "Rule Overrides",
      key: "rules",
      render: (_: unknown, record: IMarketplaceCohort) => (
        <Text style={{ fontSize: 12 }}>{summariseRules(record.rules)}</Text>
      ),
    },
    {
      title: "Actions",
      key: "actions",
      render: (_: unknown, record: IMarketplaceCohort) => (
        <Space>
          <Button size="small" onClick={() => openEdit(record)}>Edit</Button>
          <Popconfirm
            title="Delete this cohort?"
            onConfirm={() => handleDelete(record.id)}
            okText="Delete"
            okButtonProps={{ danger: true }}
          >
            <Button size="small" danger>Delete</Button>
          </Popconfirm>
        </Space>
      ),
    },
  ]

  return (
    <>
      <Divider />
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16 }}>
        <Title level={3} style={{ margin: 0 }}>Cohort Overrides</Title>
        <Button type="primary" onClick={openCreate}>Add Cohort</Button>
      </div>
      <Text type="secondary" style={{ display: "block", marginBottom: 12 }}>
        Each user is matched to exactly one cohort by account creation date.
        Rules set here override the global settings above; absent fields fall back to global defaults.
      </Text>

      <Table
        dataSource={marketplaceCohortsStore.items}
        columns={columns}
        rowKey="id"
        loading={marketplaceCohortsStore.isLoading}
        pagination={false}
      />

      <Modal
        open={modalOpen}
        title={editingCohort ? "Edit Cohort" : "Add Cohort"}
        onCancel={() => setModalOpen(false)}
        onOk={() => form.submit()}
        okText={editingCohort ? "Save" : "Create"}
        destroyOnClose
      >
        <Form form={form} layout="vertical" onFinish={handleSubmit}>
          <Form.Item name="name" label="Name" rules={[{ required: true, message: "Name is required" }]}>
            <Input placeholder="e.g. new_users" />
          </Form.Item>

          <Form.Item name="accountCreatedFrom" label="Account Created From (inclusive)">
            <DatePicker showTime style={{ width: "100%" }} placeholder="No lower bound" />
          </Form.Item>

          <Form.Item name="accountCreatedTo" label="Account Created To (exclusive)">
            <DatePicker showTime style={{ width: "100%" }} placeholder="No upper bound" />
          </Form.Item>

          <Divider orientation="left" plain style={{ fontSize: 13 }}>
            Rule Overrides <Text type="secondary" style={{ fontSize: 12 }}>(leave blank to inherit global)</Text>
          </Divider>

          {RULE_KEYS.map(({ key, label, isDollar, min, max }) => (
            <Form.Item key={key} name={key} label={label}>
              {isDollar ? (
                <InputNumber
                  min={0}
                  step={0.01}
                  style={{ width: "100%" }}
                  formatter={value => value != null ? `$ ${value}` : ""}
                  // eslint-disable-next-line @typescript-eslint/no-explicit-any
                  parser={((value: string) => parseFloat(value?.replace(/\$\s?/, "") || "") || null) as any}
                  placeholder="Inherit from global"
                />
              ) : (
                <InputNumber
                  min={min ?? 0}
                  max={max}
                  style={{ width: "100%" }}
                  placeholder="Inherit from global"
                />
              )}
            </Form.Item>
          ))}
        </Form>
      </Modal>
    </>
  )
})
